import { useEffect, useState } from 'react'

/**
 * Reactively reports whether a DOM element with the given id exists.
 *
 * Some portal containers (e.g. modal roots rendered by lazy MFE subtrees) are
 * not present in the DOM on the first render after an auth redirect. A plain
 * `document.getElementById(id)` call during render returns `null` in that
 * frame and never re-runs unless something else forces a re-render, leaving
 * portals stuck in a broken state.
 *
 * This hook:
 *  1. Performs a synchronous lookup on mount so the common (already-mounted)
 *     case has zero delay.
 *  2. Falls back to a `MutationObserver` on `document.body` that flips the
 *     flag to `true` as soon as the element is inserted anywhere in the tree,
 *     triggering a re-render.
 */
const useContainerElement = (id: string): boolean => {
  const [isPresent, setIsPresent] = useState<boolean>(() =>
    typeof document !== 'undefined' ? !!document.getElementById(id) : false
  )

  useEffect(() => {
    let observer: MutationObserver | null = null

    if (document.getElementById(id)) {
      setIsPresent(true)
    } else {
      setIsPresent(false)
      observer = new MutationObserver(() => {
        if (document.getElementById(id)) {
          setIsPresent(true)
          observer.disconnect()
        }
      })
      observer.observe(document.body, { childList: true, subtree: true })
    }

    return () => {
      if (observer) {
        observer.disconnect()
      }
    }
  }, [id])

  return isPresent
}

export default useContainerElement
